import './WaterPressureGraph.scss';

import { useIonViewDidLeave } from '@ionic/react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DqText } from 'src/components/dq-text';
import { selectIsMetric } from 'src/redux/profile-preferences/selectors';
import { useBreakpointMap } from 'src/utils/helpers/platformHelpers';
import { JobMessages } from '../../../intl/JobMessages';
import { JobGraphMask, JobPressureGraphDataElement, JobPressureGraphDataSegment } from '../util/types';
import { WaterPressureGraphController } from './WaterPressureGraphController';

interface WaterPressureGraphProps {
  /** The data to display in the graph. */
  data: JobPressureGraphDataSegment;
  /** A description of masks to draw over the graph. */
  masks?: JobGraphMask[];
  /** Whether or not timestamps are hidden. */
  hideTimestamps: boolean;
  /** The timezone associated with the job log. */
  timezone: string;
  /**
   * A function that is called when the graph is zoomed.
   * @param isModified Whether or not the zoom level differs from the initial state.
   */
  onZoom?: (isModified: boolean) => void;
}

export interface WaterPressureGraphRef {
  /** Imperatively resets the graph's zoom. */
  resetZoom: () => Promise<void>;
}

const WaterPressureGraph = forwardRef<WaterPressureGraphRef, WaterPressureGraphProps>(
  ({ data, masks, hideTimestamps, onZoom, timezone }, ref) => {
    const intl = useIntl();
    const isMetric = useSelector(selectIsMetric);
    /** Ref object that contains the element under which the chart is created. */
    const graphRef = useRef<SVGSVGElement>(null);
    /** Keeps a reference to the controller object managing the svg. */
    const [ctrlRef, setCtrlRef] = useState<WaterPressureGraphController | undefined>(undefined);
    const aspectRatio = useBreakpointMap({ xs: 1.5, sm: 2.5, md: 3.5 });

    const tooltipAccessor = useCallback(
      (at: Date | number, item: JobPressureGraphDataElement) => {
        const textLines = [
          new Date(at).toLocaleString(intl?.locale, { timeZone: timezone, timeZoneName: 'short' }),
          `${intl?.formatMessage(JobMessages.pressureLabelYAxis, {
            value: isMetric ? 'bar' : 'psi',
          })}: ${item.pressure.toFixed(3)}`,
        ];
        if (hideTimestamps) return textLines.slice(1);
        return textLines;
      },
      [hideTimestamps, intl, isMetric, timezone],
    );

    // Effect that creates the svg graph element on mount
    useEffect(() => {
      const graph = graphRef.current;
      if (!graph) {
        console.warn('The component mounted but chartRef was null! The graph will not draw.');
        return;
      }

      const ctrl = new WaterPressureGraphController(graph);
      setCtrlRef(ctrl);

      const observer = new ResizeObserver(ctrl.onResize.bind(ctrl));
      if (graph.parentElement) observer.observe(graph.parentElement);

      return () => {
        observer.disconnect();
        ctrl.setOnZoom(() => {});
        ctrl.setTooltipAccessor(() => []);
      };
    }, []);

    useIonViewDidLeave(() => {
      ctrlRef?.reset();
    }, [ctrlRef]);

    // An effect that updates the aspect ratio of the graph as the breakpoints change.
    useEffect(() => {
      ctrlRef?.setAspectRatio(aspectRatio);
    }, [aspectRatio, ctrlRef]);

    useEffect(() => {
      ctrlRef?.setDisplayConfiguration(intl, timezone, hideTimestamps);
    }, [ctrlRef, hideTimestamps, intl, timezone]);

    useEffect(() => {
      ctrlRef?.setTooltipAccessor(tooltipAccessor);
    }, [ctrlRef, tooltipAccessor]);

    useEffect(() => {
      if (onZoom) ctrlRef?.setOnZoom(onZoom);
    }, [ctrlRef, onZoom]);

    // Effect that updates the graph each time the data changes.
    useEffect(() => {
      ctrlRef?.setData(data, masks);
    }, [ctrlRef, data, hideTimestamps, isMetric, masks, timezone]);

    useImperativeHandle(
      ref,
      () => ({
        resetZoom: () => ctrlRef?.resetZoom() ?? Promise.resolve(),
      }),
      [ctrlRef],
    );

    return (
      <div className="water-pressure-graph-layout">
        <div className="graph-label" data-id="axis-label-y">
          <DqText variant="h3">
            <FormattedMessage {...JobMessages.pressureLabelYAxis} values={{ value: isMetric ? 'bar' : 'psi' }} />
          </DqText>
        </div>
        <div data-id="content">
          <svg ref={graphRef} preserveAspectRatio="xMinYMax meet" />
        </div>
        <div className="graph-label" data-id="axis-label-x">
          <DqText variant="h3">
            <FormattedMessage {...JobMessages.elapsedTime} />
          </DqText>
        </div>
      </div>
    );
  },
);

export default WaterPressureGraph;
